/**
 * `Profile` — pagina del profilo dell'utente, organizzata a blocchi.
 *
 * Rotta `/profile` (qualsiasi utente autenticato). Raccoglie le impostazioni
 * personali in sezioni indipendenti; oggi contiene il blocco "Credly" (collega
 * il proprio profilo pubblico e importa le certificazioni), predisposto per
 * ospitarne altri in futuro (es. preferenze, notifiche, integrazioni).
 *
 * Il collegamento e l'import Credly, prima nella pagina "Le mie certificazioni",
 * vivono qui: quella pagina resta il solo elenco delle certificazioni possedute.
 *
 * Endpoint usati: `PUT /api/me/credly`, `POST /api/me/certifications/import-credly`.
 * Dopo un import si invalida la lista delle certificazioni personali, così la
 * pagina `/certifications` mostra i dati aggiornati.
 */
import { type ReactNode, useEffect, useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import { useAuth } from '../auth/AuthProvider';
import type {
  ImportCredlyResponse,
  ImportLinkedinResponse,
  SetCredlyProfileRequest,
  StartLinkedinLinkResponse,
} from '../shared/dto';
import { Alert, Button, Field, PageHeader, TextInput } from '../components/ui';

/** Chiave della query delle certificazioni personali (allineata a MyCertifications). */
const myCertificationsKey = ['me', 'certifications'] as const;

/**
 * Prefisso fisso (non modificabile) dell'URL del profilo Credly. L'utente
 * inserisce solo l'ultimo segmento (la "vanity", es. `marco-piccardo`); l'URL
 * completo viene ricomposto prima di salvarlo.
 */
const CREDLY_PROFILE_PREFIX = 'https://www.credly.com/users/';

/**
 * Estrae la sola vanity (ultimo segmento) da un URL di profilo Credly salvato,
 * per precompilare il campo. Tollera l'URL completo (`.../users/<vanity>`),
 * eventuali `badges`/trailing slash e il caso in cui sia già solo la vanity.
 */
function extractCredlyVanity(stored: string): string {
  const trimmed = stored.trim();
  if (trimmed === '') return '';
  const match = /\/users\/([^/?#]+)/i.exec(trimmed);
  if (match) return decodeURIComponent(match[1]).trim();
  // Nessun `/users/`: se non sembra un URL, trattiamolo come vanity diretta.
  if (/^https?:\/\//i.test(trimmed) || trimmed.includes('/')) return '';
  return trimmed;
}

/**
 * `AccordionSection` — sezione del profilo apri/chiudi (disclosure accessibile).
 *
 * L'intestazione è un `<button aria-expanded>` che controlla la regione del
 * contenuto (`aria-controls`/`id`), coerente con gli altri accordion dell'app.
 * `defaultOpen` imposta lo stato iniziale (le sezioni sono indipendenti).
 */
function AccordionSection({
  title,
  description,
  defaultOpen = false,
  children,
}: {
  title: string;
  description?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();

  return (
    <section className="profile-accordion" aria-label={title}>
      <button
        type="button"
        className="profile-accordion__header"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="profile-accordion__heading">
          <span className="profile-accordion__title">{title}</span>
          {description !== undefined && (
            <span className="profile-accordion__description">{description}</span>
          )}
        </span>
        <span className="profile-accordion__icon" aria-hidden="true" />
      </button>
      {open && (
        <div id={contentId} className="profile-accordion__panel">
          {children}
        </div>
      )}
    </section>
  );
}

/** Blocco Credly: collega/aggiorna l'URL del profilo e importa le certificazioni. */
function CredlyBlock() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { me, refreshMe } = useAuth();

  // L'utente inserisce solo la vanity (ultimo segmento); il prefisso è fisso.
  const [credlyVanity, setCredlyVanity] = useState('');
  const [importResult, setImportResult] = useState<ImportCredlyResponse | null>(null);

  // Precompila il campo con la sola vanity estratta dall'URL già collegato
  // (quando `me` è disponibile).
  useEffect(() => {
    setCredlyVanity(extractCredlyVanity(me?.credlyProfileUrl ?? ''));
  }, [me?.credlyProfileUrl]);

  const saveCredly = useMutation({
    mutationFn: (url: string | null) => {
      const body: SetCredlyProfileRequest = { credlyProfileUrl: url };
      return api.put<void>('/api/me/credly', body);
    },
    // Ricarica `/api/me` così `credlyProfileUrl` (e quindi `hasLinkedCredly`)
    // si aggiorna subito: il pulsante "Importa da Credly" si abilita senza
    // dover ricaricare la pagina.
    onSuccess: () => {
      void refreshMe();
    },
  });

  const importCredly = useMutation({
    mutationFn: () =>
      api.post<ImportCredlyResponse>('/api/me/certifications/import-credly'),
    onSuccess: (result) => {
      setImportResult(result);
      void queryClient.invalidateQueries({ queryKey: myCertificationsKey });
    },
  });

  const hasLinkedCredly = (me?.credlyProfileUrl ?? '').trim() !== '';

  return (
    <>
      <Field
        id="credly-url"
        label="Profilo Credly"
        hint="Inserisci solo la parte finale del tuo profilo pubblico Credly, es. marco-piccardo."
      >
        <div className="competence-search">
          <div className="credly-url">
            <span className="credly-url__prefix" aria-hidden="true">
              {CREDLY_PROFILE_PREFIX}
            </span>
            <TextInput
              id="credly-url"
              className="credly-url__input"
              type="text"
              value={credlyVanity}
              onChange={(e) => setCredlyVanity(e.target.value)}
              placeholder="marco-piccardo"
              autoComplete="off"
              inputMode="text"
              aria-describedby="credly-url-prefix-note"
            />
          </div>
          <Button
            type="button"
            variant="primary"
            loading={saveCredly.isPending}
            onClick={() => {
              const vanity = credlyVanity.trim();
              saveCredly.mutate(vanity === '' ? null : `${CREDLY_PROFILE_PREFIX}${vanity}`);
            }}
          >
            Salva
          </Button>
        </div>
        <span id="credly-url-prefix-note" className="sr-only">
          L'indirizzo inizia sempre con {CREDLY_PROFILE_PREFIX}. Inserisci solo la parte
          finale.
        </span>
      </Field>

      {saveCredly.isError && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Alert tone="error">
            Profilo Credly non valido. Inserisci solo la parte finale, es.
            marco-piccardo.
          </Alert>
        </div>
      )}
      {saveCredly.isSuccess && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Alert tone="success">Profilo Credly aggiornato.</Alert>
        </div>
      )}

      <div style={{ marginTop: 'var(--space-3)' }}>
        <Button
          type="button"
          variant="default"
          loading={importCredly.isPending}
          disabled={importCredly.isPending}
          onClick={() => importCredly.mutate()}
        >
          Importa da Credly
        </Button>
        {!hasLinkedCredly && (
          <p className="ui-field__hint" style={{ marginTop: 'var(--space-2)' }}>
            Non hai collegato un profilo: proviamo comunque con lo slug
            nome-cognome. Se esiste un profilo pubblico corrispondente, lo
            colleghiamo in automatico.
          </p>
        )}
      </div>

      {importCredly.isError && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Alert tone="error" onDismiss={() => importCredly.reset()}>
            Import non riuscito. Se non hai collegato un profilo Credly, non è
            stato trovato alcun profilo pubblico con lo slug nome-cognome:
            collega manualmente il profilo e riprova.
          </Alert>
        </div>
      )}
      {importResult && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Alert tone="success" onDismiss={() => setImportResult(null)}>
            Import completato: {importResult.imported} nuove, {importResult.skipped} già
            presenti, {importResult.removed} rimosse,{' '}
            {importResult.createdCertifications} aggiunte al catalogo aziendale
            (su {importResult.fetched} badge letti).{' '}
            <Link to="/assessment/certifications">Vedi le tue certificazioni</Link>.
          </Alert>
        </div>
      )}
    </>
  );
}

/**
 * Classifica un errore delle operazioni LinkedIn in base al messaggio restituito
 * dall'API, per mostrare un avviso dedicato.
 *
 * Il backend risponde con una `ValidationError` (HTTP 400) il cui `message`
 * identifica il caso: "canale disabilitato" (config assente, Req 1.2) e
 * "ricollega LinkedIn" (token assente/scaduto/revocato, Req 4.5) non espongono
 * un `code` distinto, quindi li distinguiamo dal testo del messaggio. In
 * assenza di un match specifico ricadiamo su un messaggio generico ma utile al
 * contesto dell'azione.
 */
type LinkedinErrorKind = 'channel-disabled' | 'reconnect' | 'generic';

function classifyLinkedinError(error: unknown): LinkedinErrorKind {
  const message =
    error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const normalized = message.toLowerCase();
  // "Il canale LinkedIn non è configurato: contatta un amministratore." (Req 1.2)
  if (normalized.includes('non è configurato')) return 'channel-disabled';
  // "...: ricollega LinkedIn per importare." (token assente/scaduto/revocato, Req 4.5)
  if (normalized.includes('ricollega linkedin')) return 'reconnect';
  return 'generic';
}

/** Messaggio utente per ciascun caso d'errore LinkedIn. */
function linkedinErrorMessage(kind: LinkedinErrorKind): string {
  switch (kind) {
    case 'channel-disabled':
      return 'Il canale LinkedIn non è configurato. Contatta un amministratore per abilitarlo.';
    case 'reconnect':
      return 'La sessione LinkedIn non è più valida. Collega di nuovo LinkedIn e riprova l\u2019import.';
    default:
      return 'Operazione LinkedIn non riuscita. Riprova più tardi.';
  }
}

/**
 * Traduce il `reason` del callback OAuth (query `?linkedin=error&reason=...`) in
 * un messaggio leggibile. I `reason` noti arrivano o da LinkedIn (es.
 * `unauthorized_scope_error`, `access_denied`) o dai nostri errori di dominio
 * (es. `validation_error`), oltre a `missing_parameters` quando il callback non
 * riceve `code`/`state`.
 */
function callbackErrorMessage(reason: string): string {
  switch (reason) {
    case 'unauthorized_scope_error':
      return 'LinkedIn non ha autorizzato l’accesso ai dati richiesti per questa applicazione.';
    case 'access_denied':
    case 'user_cancelled_login':
    case 'user_cancelled_authorize':
      return 'Hai annullato il collegamento a LinkedIn.';
    case 'missing_parameters':
      return 'Risposta di LinkedIn incompleta. Riprova il collegamento.';
    case 'validation_error':
      return 'Il collegamento non è andato a buon fine (sessione non valida o scaduta). Riprova.';
    default:
      return 'Collegamento a LinkedIn non riuscito. Riprova più tardi.';
  }
}

/**
 * Blocco LinkedIn: collega l'account (OAuth), importa le certificazioni e
 * scollega. È reso **solo** in questa vista autenticata del Self_User (la pagina
 * Profilo); non compare in alcuna vista pubblica dei Candidati (Req 8.5).
 *
 * Stato del collegamento: il Frontend non ha oggi un modo per conoscere lo stato
 * "linked" dell'utente da `UserDto.linkedinLinked` (esposto da `/api/me`):
 * se non collegato mostra "Collega LinkedIn"; se collegato mostra "Importa da
 * LinkedIn" e "Scollega". Dopo il ritorno dal consenso OAuth, `refreshMe()`
 * aggiorna `me.linkedinLinked` così la UI riflette subito lo stato.
 */
function LinkedinBlock() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { me, refreshMe } = useAuth();
  const isLinked = me?.linkedinLinked === true;

  const [importResult, setImportResult] = useState<ImportLinkedinResponse | null>(null);

  // Esito del callback OAuth: al ritorno dal consenso, LinkedIn (via il nostro
  // callback pubblico) reindirizza qui con `?linkedin=connected|error&reason=...`.
  // Lo leggiamo una volta per mostrare un banner, poi puliamo la query string.
  const [searchParams, setSearchParams] = useSearchParams();
  const [callbackOutcome, setCallbackOutcome] = useState<
    { kind: 'connected' } | { kind: 'error'; reason: string } | null
  >(null);

  useEffect(() => {
    const outcome = searchParams.get('linkedin');
    if (outcome === null) return;
    if (outcome === 'connected') {
      setCallbackOutcome({ kind: 'connected' });
      void refreshMe();
    } else {
      setCallbackOutcome({ kind: 'error', reason: searchParams.get('reason') ?? '' });
    }
    // Rimuove i parametri LinkedIn dall'URL senza aggiungere una voce in history.
    const next = new URLSearchParams(searchParams);
    next.delete('linkedin');
    next.delete('reason');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, refreshMe]);

  // Collega: ottiene l'authorizationUrl e reindirizza al consenso LinkedIn.
  const startLink = useMutation({
    mutationFn: () =>
      api.post<StartLinkedinLinkResponse>('/api/me/linkedin/start'),
    onSuccess: (result) => {
      // Reindirizza l'intera pagina al flusso OAuth di LinkedIn.
      window.location.href = result.authorizationUrl;
    },
  });

  // Importa: legge le certificazioni dallo Snapshot LinkedIn e aggiorna la lista.
  const importLinkedin = useMutation({
    mutationFn: () =>
      api.post<ImportLinkedinResponse>('/api/me/certifications/import-linkedin'),
    onSuccess: (result) => {
      setImportResult(result);
      void queryClient.invalidateQueries({ queryKey: myCertificationsKey });
    },
  });

  // Scollega: rimuove token e flag lato backend, poi ricarica `/api/me`.
  const unlinkLinkedin = useMutation({
    mutationFn: () => api.delete<void>('/api/me/linkedin'),
    onSuccess: () => {
      void refreshMe();
    },
  });

  return (
    <>
      {/* Vincolo d'area: il collegamento è disponibile solo nel SEE e in
          Svizzera (Req 3.2). */}
      <Alert tone="info">
        Il collegamento LinkedIn è disponibile solo per i membri situati nello
        Spazio Economico Europeo (SEE) e in Svizzera.
      </Alert>

      {/* Esito del ritorno dal consenso OAuth (redirect del callback). */}
      {callbackOutcome?.kind === 'connected' && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Alert tone="success" onDismiss={() => setCallbackOutcome(null)}>
            LinkedIn collegato. Ora puoi importare le tue certificazioni.
          </Alert>
        </div>
      )}
      {callbackOutcome?.kind === 'error' && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Alert tone="error" onDismiss={() => setCallbackOutcome(null)}>
            {callbackErrorMessage(callbackOutcome.reason)}
          </Alert>
        </div>
      )}

      <div className="profile-linkedin__actions" style={{ marginTop: 'var(--space-3)' }}>
        {!isLinked ? (
          <Button
            type="button"
            variant="primary"
            loading={startLink.isPending}
            disabled={startLink.isPending}
            onClick={() => startLink.mutate()}
          >
            Collega LinkedIn
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="primary"
              loading={importLinkedin.isPending}
              disabled={importLinkedin.isPending}
              onClick={() => importLinkedin.mutate()}
            >
              Importa da LinkedIn
            </Button>{' '}
            <Button
              type="button"
              variant="default"
              loading={unlinkLinkedin.isPending}
              disabled={unlinkLinkedin.isPending}
              onClick={() => unlinkLinkedin.mutate()}
            >
              Scollega
            </Button>
          </>
        )}
      </div>

      {/* Errore sul "Collega": distingue il canale disabilitato (Req 1.2). */}
      {startLink.isError && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Alert tone="error" onDismiss={() => startLink.reset()}>
            {linkedinErrorMessage(classifyLinkedinError(startLink.error))}
          </Alert>
        </div>
      )}

      {/* Errore sull'"Importa": distingue canale disabilitato (Req 1.2) e
          sessione da ricollegare (Req 4.5). */}
      {importLinkedin.isError && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Alert tone="error" onDismiss={() => importLinkedin.reset()}>
            {linkedinErrorMessage(classifyLinkedinError(importLinkedin.error))}
          </Alert>
        </div>
      )}
      {importResult && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Alert tone="success" onDismiss={() => setImportResult(null)}>
            Import completato: {importResult.imported} nuove, {importResult.skipped} già
            presenti, {importResult.removed} rimosse,{' '}
            {importResult.createdCertifications} aggiunte al catalogo aziendale
            (su {importResult.fetched} certificazioni lette).{' '}
            <Link to="/assessment/certifications">Vedi le tue certificazioni</Link>.
          </Alert>
        </div>
      )}

      {unlinkLinkedin.isError && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Alert tone="error" onDismiss={() => unlinkLinkedin.reset()}>
            Scollegamento non riuscito. Riprova più tardi.
          </Alert>
        </div>
      )}
      {unlinkLinkedin.isSuccess && (
        <div style={{ marginTop: 'var(--space-3)' }}>
          <Alert tone="success" onDismiss={() => unlinkLinkedin.reset()}>
            LinkedIn scollegato. Le certificazioni già importate restano nel tuo
            profilo.
          </Alert>
        </div>
      )}
    </>
  );
}

export default function Profile() {
  return (
    <section className="profile" aria-labelledby="profile-title">
      <PageHeader
        title="Profilo"
        titleId="profile-title"
        subtitle="Gestisci le tue impostazioni personali e le integrazioni."
      />

      <div className="profile-sections">
        {/* Credly chiusa di default. La descrizione nell'header è testo semplice
            (niente link: sarebbe annidato in un button); il rimando alle
            certificazioni sta nel pannello. */}
        <AccordionSection
          title="Credly"
          description="Collega il tuo profilo pubblico Credly per importare le tue certificazioni."
          defaultOpen={false}
        >
          <p className="profile-accordion__intro">
            Le certificazioni importate le trovi in{' '}
            <Link to="/assessment/certifications">Le mie certificazioni</Link>.
          </p>
          <CredlyBlock />
        </AccordionSection>

        {/* LinkedIn chiusa di default, speculare al blocco Credly. Le azioni
            vivono solo in questa vista autenticata del Self_User (Req 8.5). */}
        <AccordionSection
          title="LinkedIn"
          description="Collega il tuo account LinkedIn per importare le tue certificazioni."
          defaultOpen={false}
        >
          <p className="profile-accordion__intro">
            Le certificazioni importate le trovi in{' '}
            <Link to="/assessment/certifications">Le mie certificazioni</Link>.
          </p>
          <LinkedinBlock />
        </AccordionSection>
      </div>
    </section>
  );
}
